import { useEffect, useState } from 'react';
import type {
  Country,
  FieldDef,
  FieldOption,
  RepeaterItem,
  RepeaterSubField,
} from '@jfh/intake-shared';
import {
  CONTACT_PRIMARY_KEY,
  COUNTRIES,
  addressComponentLabel,
  addressComponentRequired,
  countryByDialPrefix,
  flagEmoji,
  formatSortCode,
  offersPostcodeLookup,
  primaryContactIndex,
  resolveAddressComponents,
  resolveRepeaterSubFields,
  resolveStaticOptions,
} from '@jfh/intake-shared';
import { Icon } from './Icon.js';
import { Markdown } from './Markdown.js';
import { Modal } from './Modal.js';
import { SearchSelect } from './SearchSelect.js';
import { asAddress, type AddressValue } from './lib/steps.js';
import { PostcodeFinder } from './PostcodeLookup.js';
import { usePostcodeLookup } from './lib/postcodeLookup.js';

/** Sentinel for the "Other…" choice in a select. */
const OTHER = '__other__';

export interface FieldProps {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  /** Resolved value of the first address field (for "same as" on later ones). */
  firstAddress?: AddressValue;
  firstAddressLabel?: string;
  /** Enabled countries (for phone code + address country dropdowns). */
  countries: Country[];
  /** Default country ISO (phone code + address country default). */
  defaultCountryIso: string;
  /** Filled from Companies House and locked (F8): shown read-only. */
  locked?: boolean;
  /** For a `company_number` field: the lookup's status line. */
  companyStatus?: { tone: 'info' | 'ok' | 'warn' | 'error'; text: string } | null;
}

/**
 * Whether a question needs an answer: an address with required parts, or a
 * list that needs at least one entry, counts too.
 */
function needsAnswer(f: FieldDef): boolean {
  if (f.required) return true;
  if (f.type === 'address') return resolveAddressComponents(f).some((c) => c.enabled && c.required);
  if (f.type === 'repeater' || f.type === 'contact') return (f.minItems ?? 0) > 0;
  return false;
}

/** "(optional)" after an optional question's label; required ones aren't marked (F10). */
function Optional({ field }: { field: FieldDef }) {
  return needsAnswer(field) ? null : <span className="rdr-optional"> (optional)</span>;
}

/**
 * Consent field: the agreement checkbox, with any long-form terms shown in a
 * modal (rendered as Markdown) behind a "View the full terms" link rather than a
 * cramped inline box. "I agree" inside the modal ticks the box for convenience.
 */
function ConsentField({
  field: f,
  value,
  onChange,
  error,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="rdr-consent">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          {f.label}
          <Optional field={f} />
        </span>
      </label>
      {f.content && (
        <button type="button" className="rdr-link rdr-tc-open" onClick={() => setOpen(true)}>
          View the full terms &amp; conditions
        </button>
      )}
      {f.help && <div className="field-help">{f.help}</div>}
      {error && (
        <div className="rdr-error" id={`${f.id}-error`}>
          <Icon name="alert" size={13} /> {error}
        </div>
      )}
      {open && f.content && (
        <Modal
          title="Terms & conditions"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  onChange(true);
                  setOpen(false);
                }}
              >
                I agree
              </button>
            </>
          }
        >
          <Markdown text={f.content} className="rdr-md" />
        </Modal>
      )}
    </div>
  );
}

interface ControlOpts {
  firstAddress?: AddressValue;
  firstAddressLabel?: string;
  countries: Country[];
  defaultCountryIso: string;
}

export function Field({
  field: f,
  value,
  onChange,
  error,
  firstAddress,
  firstAddressLabel,
  countries,
  defaultCountryIso,
  locked,
  companyStatus,
}: FieldProps) {
  // heading is a step title, not an input; hidden never renders.
  if (f.type === 'heading' || f.type === 'hidden') return null;

  if (locked) {
    return (
      <div>
        <label className="field-label rdr-field-label">{f.label}</label>
        <div className="rdr-locked" aria-readonly="true">
          {lockedText(f, value)}
        </div>
        <div className="rdr-locked-note">
          <Icon name="check" size={13} /> From Companies House
        </div>
      </div>
    );
  }

  if (f.type === 'consent') {
    return (
      <div id={`q-${f.id}`}>
        <ConsentField field={f} value={value} onChange={onChange} error={error} />
      </div>
    );
  }

  return (
    <div id={`q-${f.id}`}>
      <label className="field-label rdr-field-label">
        {f.label}
        <Optional field={f} />
      </label>
      {renderControl(f, value, onChange, {
        firstAddress,
        firstAddressLabel,
        countries,
        defaultCountryIso,
      })}
      {companyStatus && !error && (
        <div className={`rdr-company-status ${companyStatus.tone}`} role="status">
          {companyStatus.text}
        </div>
      )}
      {f.help && <div className="field-help">{f.help}</div>}
      {error && (
        <div className="rdr-error" id={`${f.id}-error`}>
          <Icon name="alert" size={13} /> {error}
        </div>
      )}
    </div>
  );
}

/** A locked (Companies House) value as one line — an address joined up. */
function lockedText(f: FieldDef, value: unknown): string {
  if (f.type === 'address') {
    const a = asAddress(value);
    return resolveAddressComponents(f)
      .filter((c) => c.enabled && c.key !== 'country')
      .map((c) => a[c.key])
      .filter((s) => typeof s === 'string' && s.trim())
      .join(', ');
  }
  return typeof value === 'string' ? value : '';
}

function renderControl(
  f: FieldDef,
  value: unknown,
  onChange: (value: unknown) => void,
  opts: ControlOpts,
) {
  const { firstAddress, firstAddressLabel, countries, defaultCountryIso } = opts;
  const str = typeof value === 'string' ? value : '';

  switch (f.type) {
    case 'short_text':
    case 'vat':
      return (
        <input
          className="input"
          type="text"
          placeholder={f.placeholder ?? ''}
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'company_number':
      return (
        <input
          className="input"
          type="text"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={12}
          placeholder={f.placeholder || 'e.g. 01234567 or SC123456'}
          value={str}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      );
    case 'sort_code':
      return (
        <input
          className="input"
          type="text"
          inputMode="numeric"
          placeholder={f.placeholder ?? '00-00-00'}
          value={str}
          onChange={(e) => onChange(formatSortCode(e.target.value))}
        />
      );
    case 'email':
      return (
        <input
          className="input"
          type="email"
          placeholder={f.placeholder ?? ''}
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'phone':
      return (
        <PhoneControl
          value={str}
          onChange={onChange}
          countries={COUNTRIES}
          defaultIso={defaultCountryIso}
          placeholder={f.placeholder}
        />
      );
    case 'number':
      return (
        <input
          className="input"
          type="number"
          placeholder={f.placeholder ?? ''}
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'date':
      return (
        <input
          className="input"
          type="date"
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'long_text':
      return (
        <textarea
          className="textarea"
          rows={3}
          placeholder={f.placeholder ?? ''}
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'select':
      return <SelectControl field={f} value={str} onChange={onChange} />;
    case 'radio':
      return <RadioGroup field={f} value={str} onChange={onChange} />;
    case 'checkbox':
      // A single boolean checkbox; if options are present, treat as multi.
      if (f.options && f.options.length > 0) {
        return <MultiGroup field={f} value={value} onChange={onChange} />;
      }
      return (
        <label className="rdr-bool">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{f.placeholder || f.label}</span>
        </label>
      );
    case 'multiselect':
      return <MultiGroup field={f} value={value} onChange={onChange} />;
    case 'address':
      return (
        <AddressControl
          field={f}
          value={asAddress(value)}
          onChange={onChange}
          firstAddress={firstAddress}
          firstAddressLabel={firstAddressLabel}
          countries={countries}
        />
      );
    case 'file':
      return <FileDrop value={str} onChange={onChange} />;
    case 'repeater':
    case 'contact':
      return (
        <RepeaterControl
          field={f}
          value={value}
          onChange={onChange}
          countries={countries}
          defaultCountryIso={defaultCountryIso}
          firstAddress={firstAddress}
          firstAddressLabel={firstAddressLabel}
        />
      );
    default:
      return null;
  }
}

/** Phone input with a searchable country-code dropdown (value: "<dial> <number>"). */
function PhoneControl({
  value,
  onChange,
  countries,
  defaultIso,
  placeholder,
}: {
  value: string;
  onChange: (v: unknown) => void;
  countries: Country[];
  defaultIso: string;
  placeholder?: string;
}) {
  const list = countries.length ? countries : [{ iso2: 'GB', name: 'United Kingdom', dial: '+44' }];
  const matched = value ? countryByDialPrefix(value) : undefined;
  const fallback = list.find((c) => c.iso2 === defaultIso) ?? list[0]!;
  const [iso, setIso] = useState(matched?.iso2 ?? defaultIso);
  const country = list.find((c) => c.iso2 === iso) ?? fallback;
  const dial = (matched ?? country).dial;
  const number = matched ? value.slice(matched.dial.length).trim() : value.replace(/^\+\d+\s*/, '');

  const emit = (d: string, n: string) => onChange(n.trim() ? `${d} ${n.trim()}` : '');

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 'none' }}>
        <SearchSelect
          value={country.iso2}
          trigger={`${flagEmoji(country.iso2)} ${dial}`}
          items={list.map((c) => ({
            value: c.iso2,
            label: `${flagEmoji(c.iso2)} ${c.name} (${c.dial})`,
            keywords: `${c.name} ${c.dial} ${c.iso2}`.toLowerCase(),
          }))}
          onChange={(v) => {
            setIso(v);
            const d = list.find((c) => c.iso2 === v)?.dial ?? dial;
            emit(d, number);
          }}
        />
      </div>
      <input
        className="input"
        type="tel"
        style={{ flex: 1, minWidth: 0 }}
        placeholder={placeholder ?? 'Phone number'}
        value={number}
        onChange={(e) => emit(dial, e.target.value)}
      />
    </div>
  );
}

/** Searchable country dropdown (stores the country name). */
function CountrySelect({
  value,
  countries,
  onChange,
}: {
  /** The stored country CODE (iQ Code / iso2). */
  value: string;
  countries: Country[];
  /** Emits the selected country CODE (submitted to iQ; the name is shown). */
  onChange: (code: string) => void;
}) {
  const current = countries.find((c) => c.iso2 === value);
  return (
    <SearchSelect
      value={value}
      trigger={current ? `${flagEmoji(current.iso2)} ${current.name}` : value || 'Select country…'}
      items={countries.map((c) => ({
        value: c.iso2,
        label: `${flagEmoji(c.iso2)} ${c.name}`,
        keywords: `${c.name} ${c.iso2}`.toLowerCase(),
      }))}
      onChange={(code) => onChange(code)}
    />
  );
}

function OtherInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      className="input rdr-other-input"
      type="text"
      placeholder="Please specify…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus
    />
  );
}

function SelectControl({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: unknown) => void;
}) {
  const options: FieldOption[] = resolveStaticOptions(f);
  const known = options.some((o) => o.value === value);
  const [otherMode, setOtherMode] = useState(
    Boolean(f.allowOther) && !known && value !== '',
  );
  const showOther = Boolean(f.allowOther) && (otherMode || (!known && value !== ''));
  const selectValue = showOther ? OTHER : known ? value : '';
  const pick = (v: string) => {
    if (v === OTHER) {
      setOtherMode(true);
      onChange('');
    } else {
      setOtherMode(false);
      onChange(v);
    }
  };

  // iQ-backed lists can be long, so make them a searchable combobox.
  if (f.optionsSource?.type === 'iq') {
    const items = options.map((o) => ({
      value: o.value,
      label: o.label,
      keywords: o.label.toLowerCase(),
    }));
    if (f.allowOther) items.push({ value: OTHER, label: 'Other…', keywords: 'other' });
    const trigger = showOther
      ? 'Other…'
      : options.find((o) => o.value === value)?.label ?? (f.placeholder ?? 'Choose…');
    return (
      <>
        <SearchSelect value={selectValue} items={items} trigger={trigger} onChange={pick} />
        {showOther && <OtherInput value={value} onChange={onChange} />}
      </>
    );
  }

  return (
    <>
      <select
        className="select"
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === OTHER) {
            setOtherMode(true);
            onChange('');
          } else {
            setOtherMode(false);
            onChange(v);
          }
        }}
      >
        <option value="">Choose…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {f.allowOther && <option value={OTHER}>Other…</option>}
      </select>
      {showOther && <OtherInput value={value} onChange={onChange} />}
    </>
  );
}

function RadioGroup({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: unknown) => void;
}) {
  const options = resolveStaticOptions(f);
  const known = options.some((o) => o.value === value);
  const [otherMode, setOtherMode] = useState(
    Boolean(f.allowOther) && !known && value !== '',
  );
  const showOther = Boolean(f.allowOther) && (otherMode || (!known && value !== ''));
  return (
    <div className="rdr-options rdr-choices" role="radiogroup" aria-label={f.label}>
      {options.map((o) => (
        <label key={o.value} className="rdr-option">
          <input
            type="radio"
            name={f.id}
            checked={!showOther && value === o.value}
            onChange={() => {
              setOtherMode(false);
              onChange(o.value);
            }}
          />
          <span>{o.label}</span>
        </label>
      ))}
      {f.allowOther && (
        <label className="rdr-option">
          <input
            type="radio"
            name={f.id}
            checked={showOther}
            onChange={() => {
              setOtherMode(true);
              onChange('');
            }}
          />
          <span>Other</span>
        </label>
      )}
      {showOther && <OtherInput value={value} onChange={onChange} />}
    </div>
  );
}

function MultiGroup({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const options = resolveStaticOptions(f);
  const optionValues = options.map((o) => o.value);
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const known = selected.filter((s) => optionValues.includes(s));
  const custom = selected.find((s) => !optionValues.includes(s)) ?? '';
  const [otherMode, setOtherMode] = useState(
    Boolean(f.allowOther) && custom !== '',
  );
  const showOther = Boolean(f.allowOther) && (otherMode || custom !== '');

  const toggle = (v: string) => {
    const next = known.includes(v)
      ? known.filter((s) => s !== v)
      : [...known, v];
    onChange(custom ? [...next, custom] : next);
  };
  const setCustom = (text: string) =>
    onChange(text ? [...known, text] : known);

  return (
    <div className="rdr-options">
      {options.map((o) => (
        <label key={o.value} className="rdr-option">
          <input
            type="checkbox"
            checked={known.includes(o.value)}
            onChange={() => toggle(o.value)}
          />
          <span>{o.label}</span>
        </label>
      ))}
      {f.allowOther && (
        <label className="rdr-option">
          <input
            type="checkbox"
            checked={showOther}
            onChange={() => {
              if (showOther) {
                setOtherMode(false);
                onChange(known);
              } else {
                setOtherMode(true);
              }
            }}
          />
          <span>Other</span>
        </label>
      )}
      {showOther && <OtherInput value={custom} onChange={setCustom} />}
    </div>
  );
}

function addrHasData(a: AddressValue): boolean {
  return Boolean(a.line1 || a.line2 || a.line3 || a.city);
}
function addrEqual(a: AddressValue, b: AddressValue): boolean {
  return (
    a.line1 === b.line1 &&
    a.line2 === b.line2 &&
    (a.line3 ?? '') === (b.line3 ?? '') &&
    a.city === b.city &&
    a.postcode === b.postcode
  );
}
function AddressControl({
  field,
  value,
  onChange,
  firstAddress,
  firstAddressLabel,
  countries = [],
}: {
  field: Pick<FieldDef, 'address'>;
  value: AddressValue;
  onChange: (v: AddressValue) => void;
  firstAddress?: AddressValue;
  firstAddressLabel?: string;
  countries?: Country[];
}) {
  const canMirror = Boolean(firstAddress);
  const sameAsFirst = value.sameAsFirst === true;
  const components = resolveAddressComponents(field).filter((c) => c.enabled);

  // Keep a mirrored address in sync with the first address as it changes.
  useEffect(() => {
    if (sameAsFirst && firstAddress && !addrEqual(value, firstAddress)) {
      onChange({ ...firstAddress, sameAsFirst: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsFirst, firstAddress]);

  const set = (patch: Partial<AddressValue>) =>
    onChange({ ...value, ...patch, sameAsFirst: false });

  // Postcode look-up (UK addresses): find by postcode first, unless the
  // address already has lines (resumed, picked, typed) or the applicant
  // chose to type it.
  const lookup = usePostcodeLookup();
  const asksCountry = components.some((c) => c.key === 'country');
  const uk = !asksCountry || !value.country || value.country === 'GB';
  const canLookup = Boolean(lookup) && offersPostcodeLookup(field.address) && uk;
  const [mode, setMode] = useState<'find' | 'lines'>(() => (addrHasData(value) ? 'lines' : 'find'));
  const finding = canLookup && mode === 'find';

  const line = (c: (typeof components)[number]) => {
    const raw = value[c.key];
    const strVal = typeof raw === 'string' ? raw : '';
    if (c.key === 'country' && countries.length) {
      return (
        <CountrySelect
          key={c.key}
          value={strVal}
          countries={countries}
          onChange={(code) => set({ country: code } as Partial<AddressValue>)}
        />
      );
    }
    return (
      <input
        key={c.key}
        className="input"
        placeholder={
          addressComponentLabel(c) + (addressComponentRequired(c, value) ? '' : ' (optional)')
        }
        aria-label={addressComponentLabel(c)}
        value={strVal}
        onChange={(e) => set({ [c.key]: e.target.value } as Partial<AddressValue>)}
      />
    );
  };

  return (
    <div className="rdr-address">
      {canMirror && (
        <label className="rdr-option rdr-same-as">
          <input
            type="checkbox"
            checked={sameAsFirst}
            onChange={(e) =>
              e.target.checked
                ? onChange({
                    ...(firstAddress as AddressValue),
                    sameAsFirst: true,
                  })
                : onChange({ ...value, sameAsFirst: false })
            }
          />
          <span>Same as {firstAddressLabel ?? 'the first address'}</span>
        </label>
      )}

      {sameAsFirst ? (
        <div className="rdr-address-mirror">
          {firstAddress && addrHasData(firstAddress)
            ? components
                .map((c) => firstAddress[c.key])
                .filter((s) => typeof s === 'string' && s.trim())
                .join(', ')
            : 'Will use the first address above.'}
        </div>
      ) : finding && lookup ? (
        <div className="rdr-address-manual">
          {components.filter((c) => c.key === 'country').map(line)}
          <PostcodeFinder
            lookup={lookup}
            address={field.address}
            value={value}
            onPicked={(patch) => {
              set(patch);
              setMode('lines');
            }}
            onManual={() => setMode('lines')}
          />
        </div>
      ) : (
        <div className="rdr-address-manual">
          {components.map(line)}
          {canLookup && (
            <button type="button" className="rdr-link" onClick={() => setMode('find')}>
              Find the address by postcode
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Short one-line summary of a collapsed contact (first few scalar values). */
function contactSummary(subs: RepeaterSubField[], item: RepeaterItem): string {
  const parts: string[] = [];
  for (const s of subs) {
    if (s.type === 'address') continue;
    const v = item[s.id];
    if (typeof v === 'string' && v.trim()) parts.push(v.trim());
    if (parts.length >= 3) break;
  }
  return parts.join(' · ');
}

/** A blank item with every sub-field present as an empty string. */
function blankItem(subs: RepeaterSubField[]): RepeaterItem {
  const item: RepeaterItem = {};
  for (const s of subs) item[s.id] = '';
  return item;
}

function RepeaterSubControl({
  sub,
  value,
  onChange,
  defaultCountryIso,
}: {
  sub: RepeaterSubField;
  value: string;
  onChange: (v: string) => void;
  countries: Country[];
  defaultCountryIso: string;
}) {
  const ph = (sub.placeholder ?? sub.label) + (sub.required ? '' : ' (optional)');
  switch (sub.type) {
    case 'phone':
      return (
        <PhoneControl
          value={value}
          onChange={(v) => onChange(typeof v === 'string' ? v : '')}
          countries={COUNTRIES}
          defaultIso={defaultCountryIso}
          placeholder={sub.placeholder ?? sub.label}
        />
      );
    case 'long_text':
      return (
        <textarea
          className="textarea"
          rows={3}
          placeholder={ph}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'select':
      return (
        <select
          className="select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose…</option>
          {(sub.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    default: {
      const inputType =
        sub.type === 'email'
          ? 'email'
          : sub.type === 'number'
            ? 'number'
            : sub.type === 'date'
              ? 'date'
              : 'text';
      return (
        <input
          className="input"
          type={inputType}
          placeholder={sub.type === 'date' ? undefined : ph}
          aria-label={sub.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
  }
}

function RepeaterControl({
  field,
  value,
  onChange,
  countries,
  defaultCountryIso,
  firstAddress,
  firstAddressLabel,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  countries: Country[];
  defaultCountryIso: string;
  firstAddress?: AddressValue;
  firstAddressLabel?: string;
}) {
  const subs = resolveRepeaterSubFields(field);
  const itemLabel = field.itemLabel?.trim() || 'item';
  const min = field.minItems ?? (field.required ? 1 : 0);
  const max = field.maxItems;
  const isContact = field.type === 'contact';

  const current: RepeaterItem[] = Array.isArray(value)
    ? (value as RepeaterItem[])
    : [];
  // Start with at least max(minItems, 1) blank item(s) when empty.
  const items: RepeaterItem[] = current.length
    ? current
    : Array.from({ length: Math.max(min, 1) }, () => blankItem(subs));

  const canRemove = items.length > Math.max(min, 1);
  const canAdd = max === undefined || items.length < max;
  const primaryIdx = isContact ? primaryContactIndex(items) : -1;
  const setPrimary = (index: number) =>
    onChange(
      items.map((it, j) => ({ ...it, [CONTACT_PRIMARY_KEY]: j === index })),
    );

  // Contacts collapse once created — only one is expanded at a time (the latest).
  const [openIdx, setOpenIdx] = useState(items.length - 1);

  const setItemField = (index: number, subId: string, v: unknown) => {
    const next = items.map((it, i) =>
      i === index ? { ...it, [subId]: v } : it,
    );
    onChange(next);
  };
  const addItem = () => {
    onChange([...items, blankItem(subs)]);
    setOpenIdx(items.length);
  };
  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
    setOpenIdx(-1);
  };

  return (
    <div className="rdr-repeat">
      {items.map((item, i) => {
        const open = !isContact || openIdx === i;
        const summary = isContact ? contactSummary(subs, item) : '';
        return (
          <div key={i} className="rdr-repeat-item">
            <div
              className="rdr-repeat-head"
              style={isContact ? { cursor: 'pointer' } : undefined}
              onClick={isContact ? () => setOpenIdx(open ? -1 : i) : undefined}
            >
              <span className="rdr-repeat-title">
                {itemLabel} {i + 1}
                {isContact && !open && summary ? (
                  <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> — {summary}</span>
                ) : null}
              </span>
              <div
                className="rdr-repeat-head-actions"
                onClick={(e) => e.stopPropagation()}
              >
                {isContact && primaryIdx === i && (
                  <span className="rdr-primary-tag">Primary</span>
                )}
                {isContact && open && (
                  <label className="rdr-primary-pick">
                    <input
                      type="radio"
                      name={`${field.id}__primary`}
                      checked={primaryIdx === i}
                      onChange={() => setPrimary(i)}
                    />
                    <span>Primary contact</span>
                  </label>
                )}
                {canRemove && (
                  <button
                    type="button"
                    className="rdr-repeat-remove"
                    onClick={() => removeItem(i)}
                  >
                    Remove
                  </button>
                )}
                {isContact && (
                  <button
                    type="button"
                    className="rdr-repeat-toggle"
                    aria-label={open ? 'Collapse' : 'Expand'}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                  >
                    {open ? '▲' : '▼'}
                  </button>
                )}
              </div>
            </div>
            {open && (
              <div className="rdr-repeat-fields">
                {subs.map((s) => {
                  const raw = item[s.id];
                  if (s.type === 'address') {
                    return (
                      <div key={s.id} className="rdr-repeat-sub-address">
                        <label className="field-label rdr-field-label">{s.label}</label>
                        <AddressControl
                          field={s}
                          value={asAddress(raw)}
                          onChange={(v) => setItemField(i, s.id, v)}
                          countries={countries}
                          firstAddress={firstAddress}
                          firstAddressLabel={firstAddressLabel}
                        />
                      </div>
                    );
                  }
                  return (
                    <RepeaterSubControl
                      key={s.id}
                      sub={s}
                      value={typeof raw === 'string' ? raw : ''}
                      onChange={(v) => setItemField(i, s.id, v)}
                      countries={countries}
                      defaultCountryIso={defaultCountryIso}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="rdr-repeat-add"
        onClick={addItem}
        disabled={!canAdd}
      >
        + Add {itemLabel}
      </button>
    </div>
  );
}

function FileDrop({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: unknown) => void;
}) {
  // NOTE: captures the filename only — no upload yet. Uploading the bytes to
  // R2 arrives in a later build step; here we store the chosen file's name.
  return (
    <label className="rdr-dropzone">
      <input
        type="file"
        className="rdr-file-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          onChange(file ? file.name : '');
        }}
      />
      {value ? (
        <span className="rdr-file-chosen">
          <Icon name="file" size={18} />
          {value}
          <span className="rdr-file-note">(not uploaded yet)</span>
        </span>
      ) : (
        <>
          <Icon name="upload" size={20} />
          <span>Drop a file or click to browse</span>
        </>
      )}
    </label>
  );
}
